import { useEffect, useRef, useState } from 'react'
import type { GroceryItem, GroceryTitle } from '@hewey/shared'
import { Checkbox } from '@components/Checkbox.tsx'
import { useOnline } from '@hooks/useOnline.ts'
import styles from './GroceryRow.module.css'

type Props = {
	item: GroceryItem
	onTick: (next: boolean) => void
	onRename: (title: GroceryTitle) => void
}

// Only the checkbox's padded zone at the left ticks. The rest of the row is
// the title, which opens the editor, or is plain text offline.
export const GroceryRow = ({ item, onTick, onRename }: Props) => {
	const online = useOnline()
	const [editing, setEditing] = useState(false)
	const [draft, setDraft] = useState(item.title)
	const inputRef = useRef<HTMLInputElement>(null)

	useEffect(() => {
		if (editing) {
			inputRef.current?.focus()
			inputRef.current?.select()
		}
	}, [editing])

	const commit = () => {
		const trimmed = draft.trim()
		setEditing(false)
		if (trimmed.length === 0 || trimmed === item.title) return
		onRename(trimmed.slice(0, 200) as GroceryTitle)
	}

	const titleClass =
		`${styles.title} ${item.ticked ? styles.ticked : ''}`.trim()

	return (
		<li className={styles.row}>
			<Checkbox
				className={styles.check}
				checked={item.ticked}
				onChange={onTick}
				ariaLabel={item.title}
			/>
			{editing ? (
				<input
					ref={inputRef}
					className={styles.editor}
					value={draft}
					maxLength={200}
					aria-label="Item name"
					onChange={(e) => setDraft(e.target.value)}
					onBlur={commit}
					onKeyDown={(e) => {
						if (e.key === 'Enter') {
							e.preventDefault()
							commit()
						} else if (e.key === 'Escape') {
							e.preventDefault()
							setDraft(item.title)
							setEditing(false)
						}
					}}
				/>
			) : online ? (
				<button
					type="button"
					className={titleClass}
					onClick={() => {
						setDraft(item.title)
						setEditing(true)
					}}
				>
					{item.title}
				</button>
			) : (
				<span className={titleClass}>{item.title}</span>
			)}
		</li>
	)
}
